import * as React from 'react';
import { cn } from '@/lib/utils';

interface EmptyStateProps {
  title: string;
  description?: React.ReactNode;
  action?: React.ReactNode;
  /** `hero` (default) owns a whole screen. `compact` sits INSIDE a section
   *  that already has its own <h2> above it — the pages list on the Website
   *  tab, the domain notice under the tab strip — where a 3xl heading and
   *  five rems of air would shout over the heading it sits under.
   *  (Design audit T10/A12.) */
  size?: 'hero' | 'compact';
  /** Heading level. `h2` (default) is right for an empty state that IS the
   *  section — a whole screen, or a card with nothing above it. Pass `h3`
   *  when the empty state sits under a section heading that is itself an
   *  <h2>, so the outline stays a tree instead of two flat siblings.
   *  (Adversarial round B8.) */
  as?: 'h2' | 'h3';
  className?: string;
}

export function EmptyState({
  title,
  description,
  action,
  size = 'hero',
  as: Heading = 'h2',
  className,
}: EmptyStateProps) {
  // Plain block layout — earlier `flex flex-col items-start` collapsed
  // children to min-content on the cross-axis, which made every word
  // wrap to its own line inside a narrow column. Block + max-w-prose
  // lets text flow to natural line length capped at ~65ch.
  const compact = size === 'compact';
  return (
    <div className={cn('w-full max-w-prose', compact ? 'py-8' : 'py-16 sm:py-20', className)}>
      {/* B8: `text-3xl` put the hero empty state ABOVE the page's own h1 in
          visual weight — the thing that is missing shouted louder than the
          screen it is missing from. */}
      <Heading
        className={cn(
          'font-semibold tracking-tight text-foreground',
          compact ? 'text-lg' : 'text-2xl',
        )}
      >
        {title}
      </Heading>
      {description ? (
        <p
          className={cn(
            'leading-relaxed text-muted-foreground',
            compact ? 'mt-2 text-sm' : 'mt-4 text-base',
          )}
        >
          {description}
        </p>
      ) : null}
      {action ? <div className={compact ? 'mt-4' : 'mt-6'}>{action}</div> : null}
    </div>
  );
}
